import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LangContext = createContext(null);

function detectLang() {
  const saved = localStorage.getItem('lang');
  if (saved) return saved;
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  return browserLang.startsWith('zh') ? 'cn' : 'en';
}

// Translation strings
const translations = {
  en: {
    // Navbar
    'nav.ask': 'Ask',
    'nav.book': 'Book',
    'nav.patterns': 'Patterns',
    'nav.practice': 'Practice',
    'nav.pricing': 'Pricing',
    'nav.admin': 'Admin',
    'nav.guidance': 'Guidance',
    'nav.signIn': 'Sign In',
    // User menu
    'menu.account': 'Account',
    'menu.billing': 'Billing',
    'menu.signOut': 'Sign Out',
    'menu.theme': 'Theme',
    'menu.dark': 'Dark',
    'menu.light': 'Light',
    'menu.language': 'Language',
    // Ask page
    'ask.title': 'AI Life Guide',
    'ask.subtitle': 'Trained on the wisdom of all traditions',
    'ask.tryAsking': 'Try asking',
    'ask.placeholder': 'Ask anything about life, meaning, purpose...',
    'ask.limitReached': 'Daily limit reached - upgrade to continue',
    'ask.dailyLimit': 'You\'ve reached your daily free limit',
    'ask.upgradePrompt': 'Upgrade to Premium for unlimited questions',
    'ask.viewPlans': 'View Plans',
    'ask.newChat': 'New Chat',
    'ask.noChats': 'No conversations yet',
    'ask.connected': 'Connected',
    'ask.connecting': 'Connecting...',
    'ask.unlimited': 'Unlimited',
    'ask.questionsToday': 'questions today',
    'ask.opening': "What's on your mind? You can ask anything about life, meaning, suffering, love, loss, purpose, or anything else. No judgment. No dogma. Just honesty.",
    // Sample questions
    'ask.q1': 'What are the Seven Patterns that all religions share?',
    'ask.q2': 'How do I do the Three-Minute Reset?',
    'ask.q3': 'I have everything but feel nothing. Why?',
    'ask.q4': 'What happens when we die?',
    'ask.q5': 'Why do good people suffer?',
    'ask.q6': 'How does quantum physics connect to spirituality?',
    'ask.q7': 'What is the ego, and how do I quiet it?',
    'ask.q8': 'I left religion but still feel spiritual. Is that okay?',
    'ask.q9': 'What did the Buddha, Jesus, and Rumi have in common?',
    'ask.q10': 'How do I find purpose when AI can do everything?',
    'ask.q11': 'What is karma, really?',
    'ask.q12': 'How do I let go without giving up?',
    // Guidance
    'guidance.title': "I'm Going Through Something",
    'guidance.subtitle': 'You don\'t need answers right now. You need a place to start. Choose what resonates.',
    'guidance.disclaimer': 'This is not a substitute for professional help. If you\'re in crisis, please reach out to a mental health professional.',
    'guidance.relatedPatterns': 'Related Patterns',
    'guidance.tryThis': '3-Minute Exercise',
    'guidance.talkToAI': 'Talk to AI Life Guide',
    'guidance.tryPractice': 'Try the Practice',
    'guidance.closing': 'You are not broken. You are between stories. Every tradition says the same thing: the darkest moment is the closest to dawn.',
    'guidance.grief': 'Grief & Loss',
    'guidance.griefDesc': 'Someone or something you loved is gone. The world feels smaller, emptier. You don\'t know how to carry this weight.',
    'guidance.griefExerciseTitle': 'The Presence of Absence',
    'guidance.griefExercise': 'Close your eyes. Breathe three times slowly.\n\nPicture the person or thing you\'ve lost. Don\'t think about them — feel their presence.\n\nNow ask: "Where do I still feel you?"\n\nNotice where the answer lives — in your chest, your hands, a memory. That feeling is not gone. It changed form.\n\nSit with it for one minute. No fixing. Just being with what remains.',
    'guidance.loneliness': 'Loneliness & Isolation',
    'guidance.lonelinessDesc': 'You\'re surrounded by people but feel invisible. The connection you crave seems impossible to find or keep.',
    'guidance.lonelinessExerciseTitle': 'The Invisible Thread',
    'guidance.lonelinessExercise': 'Close your eyes. Breathe three times slowly.\n\nThink of one person — anyone — who has shown you kindness. A teacher, a friend, a stranger.\n\nFeel the thread that connects you to them. It\'s still there. It doesn\'t require proximity.\n\nNow widen: think of everyone who is also feeling lonely right now, in this very moment. You are part of a vast, quiet company.\n\nYou are not as alone as loneliness tells you.',
    'guidance.anxiety': 'Anxiety & Overwhelm',
    'guidance.anxietyDesc': 'Your mind won\'t stop racing. Everything feels urgent, heavy, too much. You can\'t find the off switch.',
    'guidance.anxietyExerciseTitle': 'The Three Questions',
    'guidance.anxietyExercise': 'When the spiral starts, ask yourself three things:\n\n1. "Who is having this thought?" — Notice that you can observe the anxiety. The observer is not the anxiety.\n\n2. "Is this real, or is my ego arguing with reality?" — Most anxiety is a story about a future that hasn\'t happened.\n\n3. "What is actually here, right now?" — Look at your hands. Feel the chair. Name three things you can see.\n\nThe spiral needs your attention to survive. Redirect it, even for 30 seconds.',
    'guidance.faith': 'Loss of Faith',
    'guidance.faithDesc': 'What you once believed no longer holds. The old map is gone, but the territory still feels real. You\'re between stories.',
    'guidance.faithExerciseTitle': 'The Permission Slip',
    'guidance.faithExercise': 'Take a piece of paper — or just do this in your mind.\n\nWrite: "I give myself permission to not know."\n\nThen ask: "What do I still wonder about?" Not what you were told to believe. What do YOU actually wonder?\n\nWrite down 2-3 genuine questions. These are yours. No institution owns them.\n\nThe fact that you still have questions means something in you is still searching. That\'s not faithlessness — that\'s the beginning.',
    'guidance.relationships': 'Relationship Pain',
    'guidance.relationshipsDesc': 'Love is supposed to heal, but right now it hurts. Betrayal, distance, misunderstanding, or simply growing apart.',
    'guidance.relationshipsExerciseTitle': 'The Mirror and the Window',
    'guidance.relationshipsExercise': 'Close your eyes. Breathe three times slowly.\n\nPicture the person causing your pain. Now ask two questions:\n\n"What in me is this pain reflecting?" — Not blame, but honest inquiry. What wound in you was already there before they touched it?\n\n"What do I wish they understood about me?" — Let that answer sit in your chest.\n\nNow: can you offer yourself the understanding you\'re waiting for from them?\n\nThe deepest relationships are mirrors. What you see in them lives in you.',
    'guidance.purpose': 'Career Emptiness & Purposelessness',
    'guidance.purposeDesc': 'You have the job, the routine, maybe even the success — but it feels hollow. The question "What\'s the point?" won\'t go away.',
    'guidance.purposeExerciseTitle': 'The Tuesday Morning Test',
    'guidance.purposeExercise': 'Ask yourself: "If I woke up Tuesday morning with no obligations, no reputation to protect, no salary to earn — what would I do with my day?"\n\nDon\'t edit the answer. Don\'t make it practical. Just notice what comes.\n\nNow ask: "What does that tell me about what I actually value?"\n\nPurpose is not found in job titles. It\'s found in the gap between what you do and what makes you feel alive. Notice the gap.',
    'guidance.existential': 'Existential Dread',
    'guidance.existentialDesc': 'Nothing feels real or meaningful. The big questions — death, meaning, infinity — press down with weight you can\'t shake.',
    'guidance.existentialExerciseTitle': 'The Bare Fact',
    'guidance.existentialExercise': 'When everything feels meaningless, try this:\n\nLook at your hand. Open it. Close it.\n\nYou just did something. Something moved. Something was aware of the movement.\n\nThat awareness — whatever it is — exists. Right now. Before any meaning you assign to it.\n\nThe dread comes from the stories collapsing. But beneath the stories, something is still here.\n\nYou don\'t have to know what it means. You just have to notice that it\'s here.',
    // Home
    'home.hero': 'One Light, Many Lamps',
    'home.subtitle': 'Where Ancient Wisdom Meets the Age of AI',
    // Pricing
    'pricing.title': 'Choose Your Path',
    'pricing.subtitle': 'Everyone deserves access to wisdom. Free users can ask 5 questions a day. Premium unlocks unlimited guidance.',
    'pricing.free': 'Free',
    'pricing.freeDesc': 'Begin exploring',
    'pricing.premium': 'Premium',
    'pricing.premiumDesc': 'Unlimited wisdom',
    'pricing.recommended': 'Recommended',
    'pricing.getStarted': 'Get Started',
    'pricing.upgradeNow': 'Upgrade Now',
    'pricing.signUpSubscribe': 'Sign Up & Subscribe',
    'pricing.onPremium': "You're on Premium",
    'pricing.redirecting': 'Redirecting...',
    'pricing.month': 'USD/month',
    'pricing.f1': '5 questions per day',
    'pricing.f2': 'Access to all 7 patterns',
    'pricing.f3': 'Guided meditation timer',
    'pricing.f4': 'Newsletter updates',
    'pricing.p1': 'Everything in Free',
    'pricing.p2': 'Unlimited questions',
    'pricing.p3': 'Longer messages (5,000 chars)',
    'pricing.p4': 'Multiple saved conversations',
    'pricing.p5': 'Auto-named chat history',
    'pricing.p6': 'Support the project',
    // Account
    'account.title': 'Account',
    'account.profile': 'Profile',
    'account.subscription': 'Subscription',
    'account.manageBilling': 'Manage billing',
    'account.upgradePremium': 'Upgrade to Premium',
    'account.signOut': 'Sign out',
    'account.opening': 'Opening...',
    // Billing
    'billing.title': 'Billing',
    'billing.currentPlan': 'Current Plan',
    'billing.invoices': 'Invoices',
    'billing.noInvoices': 'No invoices yet.',
    'billing.loading': 'Loading...',
    'billing.date': 'Date',
    'billing.description': 'Description',
    'billing.amount': 'Amount',
    'billing.status': 'Status',
    'billing.receipt': 'Receipt',
    'billing.invoice': 'Invoice',
    'billing.pdf': 'PDF',
    'billing.manageBilling': 'Manage Billing',
    // Auth
    'auth.createAccount': 'Create Account',
    'auth.freeQuestions': 'Start with 5 free questions per day',
    'auth.name': 'Name (optional)',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.creating': 'Creating account...',
    'auth.haveAccount': 'Already have an account?',
    'auth.signIn': 'Sign in',
    'auth.welcomeBack': 'Welcome Back',
    'auth.continueJourney': 'Sign in to continue your journey',
    'auth.signingIn': 'Signing in...',
    'auth.noAccount': "Don't have an account?",
    'auth.createOne': 'Create one',
    'auth.verifyEmail': 'Verify Your Email',
    'auth.codeSent': 'We sent a 6-digit code to',
    'auth.verificationCode': 'Verification Code',
    'auth.verifyAndContinue': 'Verify & Continue',
    'auth.verifying': 'Verifying...',
    'auth.noCode': "Didn't receive the code?",
    'auth.resend': 'Resend',
    // Book
    'book.title': 'One Light, Many Lamps',
    'book.subtitle': '万灯一光',
    'book.buyAmazon': 'Buy on Amazon',
    'book.readFree': 'Read Chapter 1 Free',
    'book.getChapter': 'Get Chapter 1 Free',
    // Footer
    'footer.quote': '"The light is one. The lamps are many."',
    'footer.author': 'Sato, with AI',
  },
  cn: {
    // Navbar
    'nav.ask': '提问',
    'nav.book': '书籍',
    'nav.patterns': '七大模式',
    'nav.practice': '修行',
    'nav.pricing': '价格',
    'nav.admin': '管理',
    'nav.guidance': '指引',
    'nav.signIn': '登录',
    // User menu
    'menu.account': '账户',
    'menu.billing': '账单',
    'menu.signOut': '退出',
    'menu.theme': '主题',
    'menu.dark': '暗色',
    'menu.light': '亮色',
    'menu.language': '语言',
    // Ask page
    'ask.title': 'AI 人生向导',
    'ask.subtitle': '汇集各传统智慧',
    'ask.tryAsking': '试试提问',
    'ask.placeholder': '关于人生、意义、目的的任何问题...',
    'ask.limitReached': '已达每日限额 - 升级以继续',
    'ask.dailyLimit': '您已达到今日免费额度',
    'ask.upgradePrompt': '升级至高级版享受无限提问',
    'ask.viewPlans': '查看方案',
    'ask.newChat': '新对话',
    'ask.noChats': '暂无对话',
    'ask.connected': '已连接',
    'ask.connecting': '连接中...',
    'ask.unlimited': '无限',
    'ask.questionsToday': '今日提问次数',
    'ask.opening': '你在想什么？你可以问任何关于生命、意义、苦难、爱、失去、目的的问题。没有评判，没有教条，只有真诚。',
    // Sample questions
    'ask.q1': '所有宗教共有的七大模式是什么？',
    'ask.q2': '如何进行三分钟重启？',
    'ask.q3': '我拥有一切，却感觉空虚。为什么？',
    'ask.q4': '死后会发生什么？',
    'ask.q5': '为什么好人会受苦？',
    'ask.q6': '量子物理如何与灵性相连？',
    'ask.q7': '什么是自我，如何让它安静？',
    'ask.q8': '我离开了宗教但仍感到灵性。这样可以吗？',
    'ask.q9': '佛陀、耶稣和鲁米有什么共同点？',
    'ask.q10': '当AI能做一切时，如何找到人生目的？',
    'ask.q11': '业力到底是什么？',
    'ask.q12': '如何放下而不放弃？',
    // Guidance
    'guidance.title': '我正在经历一些事情',
    'guidance.subtitle': '你现在不需要答案。你需要一个起点。选择与你产生共鸣的。',
    'guidance.disclaimer': '这不能替代专业帮助。如果你正处于危机中，请联系心理健康专业人士。',
    'guidance.relatedPatterns': '相关模式',
    'guidance.tryThis': '三分钟练习',
    'guidance.talkToAI': '与AI人生向导交谈',
    'guidance.tryPractice': '尝试修行',
    'guidance.closing': '你没有破碎。你在故事之间。每个传统都说同样的话：最黑暗的时刻最接近黎明。',
    'guidance.grief': '悲伤与失去',
    'guidance.griefDesc': '你爱的人或事物已经离去。世界变得更小、更空。你不知道如何承受这份重量。',
    'guidance.griefExerciseTitle': '缺席的存在',
    'guidance.griefExercise': '闭上眼睛。缓缓呼吸三次。\n\n想象你失去的人或事物。不要去想他们——感受他们的存在。\n\n然后问："我在哪里仍然感受到你？"\n\n注意答案住在哪里——在你的胸口、你的手中、一段记忆里。那种感觉没有消失。它改变了形式。\n\n静坐一分钟。不修复。只是与留下的一切在一起。',
    'guidance.loneliness': '孤独与隔离',
    'guidance.lonelinessDesc': '你被人围绕却感觉隐形。你渴望的连接似乎不可能找到或保持。',
    'guidance.lonelinessExerciseTitle': '看不见的线',
    'guidance.lonelinessExercise': '闭上眼睛。缓缓呼吸三次。\n\n想一个人——任何人——曾对你表现过善意的。一位老师、一个朋友、一个陌生人。\n\n感受连接你和他们的那根线。它还在那里。它不需要靠近。\n\n现在扩展：想想此刻也在感到孤独的每一个人。你是一个庞大而安静的群体的一部分。\n\n你并不像孤独告诉你的那样孤独。',
    'guidance.anxiety': '焦虑与压力',
    'guidance.anxietyDesc': '你的心停不下来。一切都感觉紧急、沉重、太多了。你找不到关闭的开关。',
    'guidance.anxietyExerciseTitle': '三个问题',
    'guidance.anxietyExercise': '当漩涡开始时，问自己三件事：\n\n1. "谁在有这个想法？" ——注意你可以观察焦虑。观察者不是焦虑本身。\n\n2. "这是真实的，还是我的自我在与现实争论？" ——大多数焦虑是关于尚未发生的未来的故事。\n\n3. "实际上，现在这里有什么？" ——看看你的手。感受椅子。说出你能看到的三样东西。\n\n漩涡需要你的注意力才能存活。即使只是30秒，也把它重新引导。',
    'guidance.faith': '信仰的失去',
    'guidance.faithDesc': '你曾经相信的不再成立。旧地图已经消失，但领域仍然感觉真实。你在故事之间。',
    'guidance.faithExerciseTitle': '许可条',
    'guidance.faithExercise': '拿一张纸——或者在心里做这件事。\n\n写下："我允许自己不知道。"\n\n然后问："我还在好奇什么？"不是别人告诉你要相信的。你真正好奇的是什么？\n\n写下2-3个真诚的问题。这些是你的。没有机构拥有它们。\n\n你仍然有问题这一事实意味着你内在的某些东西仍在寻找。那不是没有信仰——那是开始。',
    'guidance.relationships': '关系之痛',
    'guidance.relationshipsDesc': '爱本应治愈，但现在它在伤害。背叛、距离、误解，或者只是渐行渐远。',
    'guidance.relationshipsExerciseTitle': '镜子与窗户',
    'guidance.relationshipsExercise': '闭上眼睛。缓缓呼吸三次。\n\n想象造成你痛苦的那个人。然后问两个问题：\n\n"这种痛苦在我内心反映了什么？"——不是责怪，而是诚实的探询。在他们触碰之前，你内心已经存在什么伤口？\n\n"我希望他们理解我什么？"——让那个答案停在你的胸口。\n\n现在：你能给自己你正在等待他们给你的那份理解吗？\n\n最深的关系是镜子。你在他们身上看到的，住在你心里。',
    'guidance.purpose': '职业空虚与无目的感',
    'guidance.purposeDesc': '你有工作、日常、甚至可能有成功——但感觉空洞。"有什么意义？"这个问题挥之不去。',
    'guidance.purposeExerciseTitle': '周二早晨测试',
    'guidance.purposeExercise': '问自己："如果周二早上醒来，没有义务、不需要维护声誉、不用赚薪水——我会怎样度过这一天？"\n\n不要编辑答案。不要让它变得实际。只是注意什么出现了。\n\n然后问："这告诉我什么关于我真正重视的东西？"\n\n目的不在职位名称中。它在你所做的和让你感到活着的之间的差距中。注意这个差距。',
    'guidance.existential': '存在性恐惧',
    'guidance.existentialDesc': '一切都不再真实或有意义。死亡、意义、无限等大问题压下来，你无法摆脱。',
    'guidance.existentialExerciseTitle': '最基本的事实',
    'guidance.existentialExercise': '当一切都感觉无意义时，试试这个：\n\n看看你的手。张开。合上。\n\n你刚刚做了什么。有什么东西动了。有什么东西觉知到了这个动作。\n\n那个觉知——不管它是什么——存在。就在现在。在你赋予它任何意义之前。\n\n恐惧来自故事的崩塌。但在故事之下，有什么东西仍然在这里。\n\n你不必知道它意味着什么。你只需注意到它在这里。',
    // Home
    'home.hero': '万灯一光',
    'home.subtitle': '古老智慧遇见人工智能时代',
    // Pricing
    'pricing.title': '选择你的道路',
    'pricing.subtitle': '每个人都值得拥有智慧。免费用户每天可提问5次。高级版解锁无限指引。',
    'pricing.free': '免费',
    'pricing.freeDesc': '开始探索',
    'pricing.premium': '高级版',
    'pricing.premiumDesc': '无限智慧',
    'pricing.recommended': '推荐',
    'pricing.getStarted': '开始使用',
    'pricing.upgradeNow': '立即升级',
    'pricing.signUpSubscribe': '注册并订阅',
    'pricing.onPremium': '您已是高级版',
    'pricing.redirecting': '跳转中...',
    'pricing.month': '美元/月',
    'pricing.f1': '每天5个问题',
    'pricing.f2': '访问全部7个模式',
    'pricing.f3': '冥想计时器',
    'pricing.f4': '通讯更新',
    'pricing.p1': '包含免费版所有功能',
    'pricing.p2': '无限提问',
    'pricing.p3': '更长消息（5,000字符）',
    'pricing.p4': '多个保存的对话',
    'pricing.p5': '自动命名对话历史',
    'pricing.p6': '支持项目',
    // Account
    'account.title': '账户',
    'account.profile': '个人资料',
    'account.subscription': '订阅',
    'account.manageBilling': '管理账单',
    'account.upgradePremium': '升级至高级版',
    'account.signOut': '退出登录',
    'account.opening': '打开中...',
    // Billing
    'billing.title': '账单',
    'billing.currentPlan': '当前方案',
    'billing.invoices': '发票',
    'billing.noInvoices': '暂无发票。',
    'billing.loading': '加载中...',
    'billing.date': '日期',
    'billing.description': '描述',
    'billing.amount': '金额',
    'billing.status': '状态',
    'billing.receipt': '收据',
    'billing.invoice': '发票',
    'billing.pdf': 'PDF',
    'billing.manageBilling': '管理账单',
    // Auth
    'auth.createAccount': '创建账户',
    'auth.freeQuestions': '每天开始5个免费提问',
    'auth.name': '姓名（可选）',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.creating': '创建中...',
    'auth.haveAccount': '已有账户？',
    'auth.signIn': '登录',
    'auth.welcomeBack': '欢迎回来',
    'auth.continueJourney': '登录以继续你的旅程',
    'auth.signingIn': '登录中...',
    'auth.noAccount': '没有账户？',
    'auth.createOne': '创建一个',
    'auth.verifyEmail': '验证您的邮箱',
    'auth.codeSent': '我们已发送6位验证码至',
    'auth.verificationCode': '验证码',
    'auth.verifyAndContinue': '验证并继续',
    'auth.verifying': '验证中...',
    'auth.noCode': '没有收到验证码？',
    'auth.resend': '重新发送',
    // Book
    'book.title': '万灯一光',
    'book.subtitle': 'One Light, Many Lamps',
    'book.buyAmazon': '在亚马逊购买',
    'book.readFree': '免费阅读第一章',
    'book.getChapter': '免费获取第一章',
    // Footer
    'footer.quote': '"光是一。灯是万。"',
    'footer.author': 'Sato（悟），与AI合著',
  },
};

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem('lang', l);
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations.en[key] || key;
  }, [lang]);

  useEffect(() => {
    document.documentElement.lang = lang === 'cn' ? 'zh-CN' : 'en';
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within LangProvider');
  return ctx;
}
